<script lang="ts">
  import { onMount } from 'svelte';
  import { Router, goto } from '@mateothegreat/svelte5-router';
  import AdaptiveNavigation from './AdaptiveNavigation.svelte';
  import DesktopHeader from './DesktopHeader.svelte';
  import PromoSidebar from './PromoSidebar.svelte';
  import ServiceWorkerRegistration from './ServiceWorkerRegistration.svelte';
  import Library from './Library.svelte';
  import Reader from './Reader.svelte';
  import Settings from './Settings.svelte';
  import Profile from './Profile.svelte';
  import { navigation } from '../stores/navigation';

  let currentRoute = $state('/');
  let isReaderRoute = $state(false);
  let isMobile = $state(true);

  const MOBILE_BREAKPOINT = 600;

  onMount(() => {
    // Restore last route or go to library
    const initialRoute = navigation.getInitialRoute();
    if (initialRoute && initialRoute !== '/') {
      goto(initialRoute);
    }

    // Track route changes via History API events
    const updateRoute = () => {
      currentRoute = window.location.pathname + window.location.search + window.location.hash.replace(/^#\/?/, '/');
      isReaderRoute = currentRoute.startsWith('/reader');
      navigation.setRoute(currentRoute);
    };
    updateRoute();
    window.addEventListener('pushState', updateRoute);
    window.addEventListener('replaceState', updateRoute);
    window.addEventListener('popstate', updateRoute);
    window.addEventListener('hashchange', updateRoute);

    // Handle window resize
    function handleResize() {
      isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    }

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('pushState', updateRoute);
      window.removeEventListener('replaceState', updateRoute);
      window.removeEventListener('popstate', updateRoute);
      window.removeEventListener('hashchange', updateRoute);
      window.removeEventListener('resize', handleResize);
    };
  });

  const routes = [
    { path: '/', component: Library },
    { path: '^/reader(?:/(?<bookId>[^/?#]+))?$', component: Reader },
    { path: '/settings', component: Settings },
    { path: '/profile', component: Profile },
    { path: '/.*/', component: Library }
  ];
</script>

{#if isMobile}
  <!-- Mobile layout -->
  <!-- Navigation: bottom bar -->
  <AdaptiveNavigation />

  <!-- Main content -->
  {#if isReaderRoute}
    <Router {routes} />
  {:else}
    <main class="px-4 py-4 pb-20 min-h-screen">
      <Router {routes} />
    </main>
  {/if}
{:else}
  <!-- Desktop layout: fixed structure with header, content area, footer -->
  <div class="fixed inset-0 flex flex-col">
    <!-- Header -->
    <DesktopHeader />

    <!-- Middle section: Nav Rail + Content -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Navigation Rail -->
      <AdaptiveNavigation />

      <!-- Content area -->
      {#if isReaderRoute}
        <!-- Reader with its own internal structure -->
        <div class="flex-1 overflow-hidden">
          <Router {routes} />
        </div>
      {:else}
        <!-- Library/Settings/Profile with Promo Sidebar -->
        <main class="flex-1 overflow-y-auto">
          <div class="container mx-auto px-6 py-8 max-w-6xl">
            <Router {routes} />
          </div>
        </main>

        <!-- Right Promo Sidebar (desktop only, non-reader pages) -->
        <PromoSidebar />
      {/if}
    </div>

    <!-- Footer at bottom, full width -->
    <footer class="border-t dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
      <div class="container mx-auto px-6 py-4">
        <div class="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div class="flex items-center space-x-4">
            <span class="font-semibold text-gray-900 dark:text-gray-100">Legere</span>
            <span>About</span>
            <span>Policy</span>
            <span>Contacts</span>
            <span>...</span>
          </div>
          <div class="flex items-center space-x-4">
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" class="hover:text-gray-900 dark:hover:text-gray-100">
              X Github
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" class="hover:text-gray-900 dark:hover:text-gray-100">
              Facebook
            </a>
          </div>
        </div>
      </div>
    </footer>
  </div>
{/if}

<!-- Service Worker Registration (appears on all pages) -->
<ServiceWorkerRegistration />
